import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { APP_BRANDING } from '../../utils/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Email Verification Component
 * Handles email verification via token from URL parameters
 */
const EmailVerification = () => {
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      // Get token from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await axios.post(`${BACKEND_URL}/api/auth/verify-email`, {
          token: token
        });

        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
        
        // Redirect to login page after 5 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 5000);

      } catch (error) {
        setStatus('error');
        setMessage(
          error.response?.data?.detail || 
          'An error occurred during email verification. Please try again or contact support.'
        );
      }
    };

    verifyEmail();
  }, []);

  const handleLoginRedirect = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">🚗</h1>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Email Verification
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Verifying your email...
              </h3>
              <p className="text-sm text-gray-600">
                Please wait while we verify your email address.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="text-green-600 text-6xl mb-4">✓</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Email Verified Successfully!
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {message}
              </p>
              {email && (
                <p className="text-sm text-gray-500 mb-6">
                  Account: <span className="font-medium">{email}</span>
                </p>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                <p className="text-sm text-green-800">
                  🎉 <strong>Welcome to {APP_BRANDING.name}!</strong><br/>
                  Your account is now active and ready to use.
                </p>
              </div>

              <button
                onClick={handleLoginRedirect}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Continue to Login
              </button>
              
              <p className="text-xs text-gray-500 mt-4">
                Redirecting automatically in 5 seconds...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="text-red-600 text-6xl mb-4">✕</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Verification Failed
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {message}
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  Possible reasons:
                </h4>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>The verification link has expired (24 hours)</li>
                  <li>The link has already been used</li>
                  <li>The link is invalid or corrupted</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleLoginRedirect}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Go to Login
                </button>
                
                <p className="text-xs text-gray-500">
                  Need help? Contact support through the login page.
                </p>
              </div>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;